import React, { useMemo, useState } from 'react';
import {
    Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from 'recharts';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { X, Layers, BarChart3, Loader2, LayoutDashboard } from 'lucide-react';
import UnderlinedTabs from '@/components/tabs';
import { useTenant } from '@/hooks';
import { useSearchParams } from 'react-router-dom';
import {
  useDeleteProjectSkillMutation,
  useGetInternalProjectSkillsQuery,
  useGetExternalProjectSkillsQuery,
  useDeleteExternalProjectSkillMutation
} from '@/state';
import { toast } from 'sonner';

interface SkillVisualizerProps {
    projectId: number;
    project_id?: number; // Core project ID for external
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const SkillVisualizer: React.FC<SkillVisualizerProps> = ({ projectId, project_id }) => {
    const { tenant } = useTenant();
    const [searchParams] = useSearchParams();
    const isExternal = searchParams.get('type') === 'EXTERNAL';

    const { data: internalSkills, isLoading: isFetchingInternal } = useGetInternalProjectSkillsQuery(
        { tenant: tenant!, projectId },
        { skip: isExternal }
    );
    const { data: externalSkills, isLoading: isFetchingExternal } = useGetExternalProjectSkillsQuery(
        { tenant: tenant!, projectId: project_id || projectId },
        { skip: !isExternal }
    );

    const skills = isExternal ? externalSkills : internalSkills;
    const isFetching = isExternal ? isFetchingExternal : isFetchingInternal;

    const [deleteInternalSkill, { isLoading: isDeletingInternal }] = useDeleteProjectSkillMutation();
    const [deleteExternalSkill, { isLoading: isDeletingExternal }] = useDeleteExternalProjectSkillMutation();

    const isDeleting = isExternal ? isDeletingExternal : isDeletingInternal;
    const [activeTab, setActiveTab] = useState('list');

    const chartData = useMemo(() => {
        return (skills || []).map(s => ({
            subject: s.skill_name,
            level: s.required_level,
            fullMark: 5,
        }));
    }, [skills]);

    const handleDelete = async (skillId: number, name: string) => {
        try {
            if (isExternal) {
                await deleteExternalSkill({
                    tenant: tenant!,
                    id: skillId,
                    projectId: project_id || projectId
                }).unwrap();
            } else {
                await deleteInternalSkill({
                    tenant: tenant!,
                    id: skillId,
                    projectId
                }).unwrap();
            }
            toast.success(`${name} removed from project`);
        } catch (err) {
            toast.error('Failed to remove skill');
        }
    };

    if (isFetching) {
        return (
            <div className="flex items-center justify-center py-12 mt-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!skills || skills.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-muted rounded-xl bg-muted/5 mt-8">
                <LayoutDashboard className="w-12 h-12 text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground italic text-sm">No skills added to this project yet.</p>
            </div>
        );
    }

    const tabs = [
        {
            value: 'list',
            title: 'Skill List',
            icon: <Layers size={16} />,
            content: (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                    {skills.map((skill, index) => (
                        <div
                            key={skill.id || index}
                            className="group relative overflow-hidden flex flex-col p-6 rounded-xl bg-card border border-border/40 hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/5 transition-all duration-500"
                        >
                            <div className="absolute -right-4 -top-4 w-24 h-24 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-all duration-500" />

                            <div className="flex items-start justify-between mb-6">
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-base font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-tight">
                                        {skill.skill_name}
                                    </h4>
                                    <p className="text-[10px] font-medium text-muted-foreground uppercase mt-1 tracking-wider opacity-70">Competency Level</p>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <button
                                        onClick={() => skill.id && handleDelete(skill.id, skill.skill_name)}
                                        disabled={!skill.id || isDeleting}
                                        className="p-2 rounded-lg z-50 bg-muted/50 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all opacity-0 group-hover:opacity-100 disabled:opacity-0"
                                    >
                                        <X size={14} />
                                    </button>
                                    <div className="shrink-0 flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 text-primary font-black text-lg border border-primary/20 shadow-inner group-hover:scale-110 transition-transform duration-500">
                                        {skill.required_level}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-auto">
                                <div className="flex gap-1.5 h-1.5 w-full">
                                    {[1, 2, 3, 4, 5].map((l) => (
                                        <div
                                            key={l}
                                            className={cn(
                                                "flex-1 rounded-full transition-all duration-500",
                                                l <= skill.required_level
                                                    ? "bg-primary shadow-[0_0_8px_rgba(var(--primary-rgb),0.3)]"
                                                    : "bg-muted/30"
                                            )}
                                            style={{
                                                transitionDelay: `${l * 50}ms`
                                            }}
                                        />
                                    ))}
                                </div>
                                <div className="flex justify-between mt-2 px-0.5">
                                    <span className="text-[9px] font-bold text-muted-foreground/60 tracking-tighter uppercase leading-none mt-1">Basic</span>
                                    <span className="text-[9px] font-bold text-primary/60 tracking-widest uppercase leading-none mt-1">Expert</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )
        },
        {
            value: 'charts',
            title: 'Skill Analytics',
            icon: <BarChart3 size={16} />,
            content: (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                    <Card className="p-8 bg-background/50 backdrop-blur-md border-border/40 shadow-xl rounded-xl group relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-8 opacity-5">
                            <Layers className="w-24 h-24" />
                        </div>
                        <h3 className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground mb-8">Skill Profile (Radar View)</h3>
                        <div className="h-[400px] w-full text-foreground transition-all duration-1000 animate-in fade-in zoom-in-95">
                            <ResponsiveContainer width="100%" height="100%">
                                <RadarChart cx="50%" cy="50%" outerRadius="75%" data={chartData}>
                                    <defs>
                                        <linearGradient id="radarGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.4} />
                                            <stop offset="95%" stopColor="var(--primary)" stopOpacity={0.1} />
                                        </linearGradient>
                                    </defs>
                                    <PolarGrid 
                                        stroke="var(--foreground)" 
                                        strokeOpacity={0.1} 
                                        strokeDasharray="4 4"
                                    />
                                    <PolarAngleAxis
                                        dataKey="subject"
                                        tick={{ 
                                            fill: 'var(--muted-foreground)', 
                                            fontSize: 10, 
                                            fontWeight: 600,
                                            letterSpacing: '0.05em'
                                        }}
                                    />
                                    <PolarRadiusAxis
                                        angle={30}
                                        domain={[0, 5]}
                                        tick={false}
                                        axisLine={false}
                                    />
                                    <Radar
                                        name="Required Level"
                                        dataKey="level"
                                        stroke="var(--primary)"
                                        strokeWidth={3}
                                        fill="url(#radarGradient)"
                                        fillOpacity={1}
                                        dot={{ 
                                            fill: 'var(--primary)', 
                                            r: 4, 
                                            strokeWidth: 2, 
                                            stroke: 'var(--background)' 
                                        }}
                                        activeDot={{
                                            r: 6,
                                            stroke: 'var(--primary)',
                                            strokeWidth: 2,
                                            fill: 'var(--background)'
                                        }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: 'var(--popover)',
                                            borderColor: 'var(--border)',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            boxShadow: '0 20px 50px -12px rgba(0,0,0,0.5)',
                                            backdropFilter: 'blur(12px)',
                                            border: '1px solid var(--border)',
                                            color: 'var(--popover-foreground)',
                                            padding: '12px'
                                        }}
                                        itemStyle={{ 
                                            color: 'var(--primary)', 
                                            fontWeight: 700 
                                        }}
                                        cursor={{ stroke: 'var(--primary)', strokeWidth: 1, strokeDasharray: '4 4' }}
                                    />
                                </RadarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>

                    <Card className="p-8 bg-background/50 backdrop-blur-md border-border/40 shadow-xl rounded-xl group relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-8 opacity-5">
                            <BarChart3 className="w-24 h-24" />
                        </div>
                        <h3 className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground mb-8">Competency Benchmarks</h3>
                        <div className="h-[400px] w-full text-foreground transition-all duration-1000 animate-in fade-in zoom-in-95">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={chartData}
                                    layout="vertical"
                                    margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border)" strokeOpacity={0.2} />
                                    <XAxis type="number" domain={[0, 5]} hide />
                                    <YAxis
                                        dataKey="subject"
                                        type="category"
                                        tick={{ 
                                            fill: 'var(--muted-foreground)', 
                                            fontSize: 11, 
                                            fontWeight: 700 
                                        }}
                                        width={140}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip
                                        cursor={{ fill: 'var(--primary)', opacity: 0.05 }}
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length) {
                                                return (
                                                    <div className="bg-popover/90 backdrop-blur-xl border border-border p-4 rounded-xl shadow-2xl animate-in fade-in zoom-in-95">
                                                        <p className="font-black text-popover-foreground text-xs uppercase tracking-widest mb-2 border-b border-border pb-2">
                                                            {payload[0].payload.subject}
                                                        </p>
                                                        <div className="flex items-center justify-between gap-4">
                                                            <div className="flex items-center gap-2">
                                                                <div className="h-2 w-2 rounded-full bg-primary shadow-[0_0_8px_var(--primary)]" />
                                                                <p className="text-muted-foreground text-[10px] font-bold uppercase">Required Level</p>
                                                            </div>
                                                            <p className="text-primary font-black text-lg leading-none">{payload[0].value}</p>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Bar dataKey="level" radius={[0, 6, 6, 0]} barSize={28}>
                                        {chartData.map((_, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={COLORS[index % COLORS.length]}
                                                fillOpacity={0.7}
                                                className="hover:fill-opacity-100 transition-all duration-300 cursor-pointer"
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </div>
            )
        }
    ];

    return (
        <div className="mt-8">
            <UnderlinedTabs
                tabItems={tabs}
                subTab
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full"
            />
        </div>
    );
};

export default SkillVisualizer;
